/**
 * ParentShellHeader — l'adaptateur entre la coquille legacy et le `<ParentHeader>` du DS.
 *
 * C'est de la chrome d'espace, pas un écran : il n'ouvre aucun écran et ne charge rien. Il prend
 * ce que `ParentLayoutContext` fournit déjà (les enfants de la famille, l'enfant sélectionné, la
 * bascule en cours, les gestes) — passé en props par le pont legacy, parce que `ports/**` ne
 * peut pas importer `src/common/**` — lit dans le store le prénom du parent, et rend le header du
 * DS. Le cœur du parrainage l'a quitté (Wajih, 28/09) : le bandeau en bas de l'accueil et la ligne
 * du portail Abonnement l'ont remplacé (`useReferralEntry`).
 *
 * Il monte aussi la feuille « Changer de profil » du DS et tient son ouverture. C'est la seule
 * chose qu'il possède : la visibilité d'une surface est de l'état d'interface, pas du métier.
 *
 * **Le bouton d'aide** (Wajih, 28/09 : il remplace la bulle flottante) : c'est ici, et nulle part
 * ailleurs, qu'il est branché — l'ouverture de la messagerie descendue par la coquille
 * (`openSupportChat`, `useOpenIntercomChat()`), le SDK prêt et les non-lus lus par
 * `useIntercomAdapter`. La bulle de support est transitoire (un jour, un menu « Contacter le
 * support ») : la retirer, c'est ne plus passer `onContactSupport` au header.
 *
 * La feuille porte la section « Apparence » : la préférence vient du magasin de l'appareil
 * (`parentAppearancePreference`), que `useParentThemeScope` lit de son côté.
 *
 * Pendant une bascule d'enfant, le header ne montre rien qui tourne : la chip touchée est déjà la
 * sélectionnée, chips et pastille ne répondent plus (`<ParentHeader switchingChildId>`), et c'est
 * la page qui montre son squelette.
 */
import React, { useMemo, useState } from "react"
import { useSelector } from "react-redux"

import parentIcon from "../../../../../../assets/avatar/default/parent-512.png"
import { ParentHeader, ProfileSwitcherSheet } from "../../../../../design-system"
import { toUIParentChildren } from "../../../adapters/parent/shared/parentChildAdapter"
import { parentShellViewModel } from "../../../view-models/parent/ParentShell/parentShellViewModel"
import { setParentAppearancePreference, useParentAppearancePreference } from "../../hooks/parentAppearancePreference"
import { useIntercomAdapter } from "../../hooks/useIntercomAdapter"

export type ParentShellHeaderProps = {
    /** Comptes élèves de la famille, tels que `ParentMainLayout` les a calculés. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    familyAccounts?: any[]
    selectedChildId?: string | number
    switchingChildId?: string | number | null
    /** Bascule vers un autre enfant (legacy `handleUserSelection`, appelé avec l'identifiant). */
    onSelectChildId?: (id: string | number) => void
    /** Quitte le mode parent pour l'espace élève de cet enfant (legacy `handleExitToChild`). */
    onExitToChildId?: (id: string | number) => void
    /** Sous impersonation, la pastille reste décorative (règle du header legacy). */
    profileDisabled?: boolean
    /**
     * « Se déconnecter » : la déconnexion de la coquille (legacy `startLogout`), qui
     * ramène sur « Qui utilise Masteur ? ». Absent → la feuille n'a pas de pied.
     */
    onSwitchAccount?: () => void
    /** La déconnexion est en cours (legacy `isLoggingOut`). */
    switchingAccount?: boolean
    /** Ouvre la messagerie du support (legacy `openSupportChat`). Absent → pas de bouton d'aide. */
    onContactSupport?: () => void
}

export function ParentShellHeader(props: ParentShellHeaderProps) {
    const {
        familyAccounts,
        selectedChildId,
        switchingChildId = null,
        onSelectChildId,
        onExitToChildId,
        profileDisabled = false,
        onSwitchAccount,
        switchingAccount = false,
        onContactSupport,
    } = props

    // Le view-model est mémoïsé sur la CHAÎNE qu'il rend (`parentShellViewModel`) : le header ne
    // se redessine donc que si le prénom du parent change, et non à chaque rechargement du compte.
    const { parentFirstname } = useSelector(parentShellViewModel)
    const [switcherVisible, setSwitcherVisible] = useState(false)
    const appearance = useParentAppearancePreference()
    const support = useIntercomAdapter({ onOpen: onContactSupport })

    // Le header est monté sous les quatre onglets : sans ce garde-fou, la liste des enfants se
    // reconstruisait à chaque rendu, et deux surfaces la recevaient en prop.
    const children = useMemo(() => toUIParentChildren(familyAccounts), [familyAccounts])

    return (
        <>
            <ParentHeader
                parentFirstname={parentFirstname}
                profileSource={parentIcon}
                onSwitchProfile={profileDisabled ? undefined : () => setSwitcherVisible(true)}
                profileDisabled={profileDisabled}
                childProfiles={children}
                selectedChildId={selectedChildId}
                switchingChildId={switchingChildId}
                onSelectChild={(child) => onSelectChildId?.(child.id)}
                onContactSupport={support.onContactSupport}
                supportUnreadCount={support.unreadCount}
            />

            {/* Ni `selectedProfileId` ni « Mon compte » : dans l'espace parent, `ProfileSwitcherM`
                calcule `isSelected = !isParentMode && …`, donc aucune ligne enfant n'est active et
                le lien vers la fiche compte n'y existe pas. Le profil actif, c'est le parent. */}
            <ProfileSwitcherSheet
                visible={switcherVisible}
                onClose={() => setSwitcherVisible(false)}
                profiles={children}
                switchingProfileId={switchingChildId}
                onSelectProfile={(profile) => onExitToChildId?.(profile.id)}
                parentAvatarSource={parentIcon}
                onSwitchAccount={onSwitchAccount}
                switchingAccount={switchingAccount}
                appearance={{ value: appearance, onChange: setParentAppearancePreference }}
            />
        </>
    )
}
